import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getQuizById, getQuizAttemptById } from '../../services/firestoreService';
import { SafeMathText } from '../MakeQuiz/MathRenderer';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  CheckCircle, XCircle, ArrowLeft,
  ChevronDown, ChevronUp
} from 'lucide-react';
import checkmarkIcon from '../../Assets/icons/checkmark-complete-correct-svgrepo-com.svg';

// Custom animations
const animationStyles = `
  @keyframes bounceShort {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-bounce-short {
    animation: bounceShort 0.6s ease-in-out;
  }
  .animate-fade-in-up {
    animation: fadeInUp 0.5s ease-out;
  }
`;

const QuizReview = () => {
  const { quizId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [quiz, setQuiz] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeSpent, setTimeSpent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        // Check if data was passed via navigation state
        if (location.state) {
          setQuiz(location.state.quiz);
          setScoreData(location.state.scoreData);
          setUserAnswers(location.state.userAnswers);
          setTimeSpent(location.state.timeSpent);
          setLoading(false);
          return;
        }

        // Otherwise, load from Firestore (if we have attemptId in URL params)
        const params = new URLSearchParams(location.search);
        const attemptId = params.get('attemptId');

        if (!attemptId) {
          notify({
            title: 'Error',
            message: 'No attempt data found',
            type: 'error',
          });
          navigate('/main');
          return;
        }

        // Load quiz and attempt data
        const [quizResult, attemptResult] = await Promise.all([
          getQuizById(quizId),
          getQuizAttemptById(attemptId)
        ]);

        if (!quizResult.success || !attemptResult.success) {
          throw new Error('Failed to load data');
        }

        setQuiz(quizResult.data);
        setUserAnswers(attemptResult.data.userAnswers || {});
        setScoreData({
          totalScore: attemptResult.data.score,
          maxScore: attemptResult.data.maxScore,
          percentage: attemptResult.data.percentage,
          questionResults: attemptResult.data.questionResults
        });
        setTimeSpent(attemptResult.data.timeSpent);
        setLoading(false);
      } catch (error) {
        console.error('Error loading review data:', error);
        notify({
          title: 'Error',
          message: 'Failed to load quiz review',
          type: 'error',
        });
        navigate('/main');
      }
    };

    loadData();
  }, [quizId, location, navigate, notify]);

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedQuestions(new Set(quiz.questions.map(q => q.id)));
  };

  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  const handleGoHome = () => {
    navigate('/main');
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0m 0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 70) return 'text-blue-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceLabel = (percentage) => {
    if (percentage >= 90) return 'Excellent!';
    if (percentage >= 70) return 'Good Job!';
    if (percentage >= 50) return 'Passing';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg font-medium">Loading Results...</p>
        </div>
      </div>
    );
  }

  if (!quiz || !scoreData) {
    return null;
  }

  const correctCount = scoreData.questionResults?.filter(r => r.isCorrect).length || 0;
  const incorrectCount = quiz.questions.length - correctCount;

  // Determine if user passed based on quiz settings
  const passingScore = quiz.settings?.passingScore || 50;
  const hasPassingScore = quiz.settings?.hasPassingScore || false;
  const hasPassed = !hasPassingScore || scoreData.percentage >= passingScore;

  return (
    <>
      {/* Inject animation styles */}
      <style>{animationStyles}</style>

      <div className="w-full min-h-screen bg-slate-50 flex flex-col">
        {/* Minimal Top Header */}
        <header className="w-full bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <button
          onClick={handleGoHome}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Exit Review</span>
        </button>
        <span className="font-semibold text-slate-800 text-sm sm:text-base truncate max-w-xs sm:max-w-md">
          Review: {quiz.title}
        </span>
        {/* Empty div to maintain spacing */}
        <div className="w-4 h-4"></div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto w-full">
          {/* Score Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 sm:p-8 mb-6">
              <div className="text-center mb-6">
                <div className="mx-auto mb-4 flex justify-center">
                  <img
                    src={checkmarkIcon}
                    alt="Quiz Complete"
                    className="w-20 h-20 sm:w-24 sm:h-24 object-contain drop-shadow-lg"
                  />
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                  Quiz Complete!
                </h1>
                <p className="text-slate-600 text-sm sm:text-base">{quiz.title}</p>
              </div>

              {/* Score Display - Conditional animation based on pass/fail */}
              <div className={`rounded-xl p-6 mb-6 transition-all duration-500 ${
                hasPassed
                  ? 'bg-gradient-to-br from-emerald-50 to-teal-50'
                  : 'bg-slate-50'
              }`}>
                <div className={`text-center ${hasPassed ? 'animate-bounce-short' : 'animate-fade-in-up'}`}>
                  <p className={`text-5xl sm:text-6xl md:text-7xl font-bold mb-2 ${
                    hasPassed
                      ? getPerformanceColor(scoreData.percentage)
                      : 'text-slate-700'
                  }`}>
                    {correctCount} / {quiz.questions.length}
                  </p>
                  <p className="text-lg sm:text-xl font-semibold text-slate-600 mb-3">
                    Correct
                  </p>
                  <span className={`inline-block px-4 py-1.5 text-sm sm:text-base font-medium rounded-full ${
                    hasPassed
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-200 text-slate-700'
                  }`}>
                    {getPerformanceLabel(scoreData.percentage)}
                  </span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-slate-50 rounded-lg p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{quiz.questions.length}</p>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">Total Questions</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-emerald-600">{correctCount}</p>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">Correct</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{incorrectCount}</p>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">Incorrect</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">{formatTime(timeSpent)}</p>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">Time Taken</p>
                </div>
              </div>
          </div>

          {/* Review Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                Answer Review
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Collapse All
                </button>
              </div>
          </div>

          {/* Questions Review */}
          <div className="space-y-4">
              {quiz.questions.map((question, index) => {
                const result = scoreData.questionResults?.find(r => r.questionId === question.id);
                const isCorrect = result?.isCorrect || false;
                const isExpanded = expandedQuestions.has(question.id);
                const userAnswer = userAnswers[question.id];

                return (
                  <QuestionReviewCard
                    key={question.id}
                    question={question}
                    questionNumber={index + 1}
                    isCorrect={isCorrect}
                    userAnswer={userAnswer}
                    pointsEarned={result?.pointsEarned || 0}
                    isExpanded={isExpanded}
                    onToggle={() => toggleQuestion(question.id)}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// Question Review Card Component
const QuestionReviewCard = ({
  question,
  questionNumber,
  isCorrect,
  userAnswer,
  pointsEarned,
  isExpanded,
  onToggle
}) => {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden transition-all ${
        isCorrect ? 'border-green-200' : 'border-red-200'
      }`}
    >
      {/* Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          {/* Status Icon */}
          <div className={`flex-shrink-0 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
            {isCorrect ? (
              <CheckCircle className="w-6 h-6" />
            ) : (
              <XCircle className="w-6 h-6" />
            )}
          </div>

          {/* Question Info */}
          <div className="text-left">
            <p className="font-semibold text-gray-900">
              Question {questionNumber}
            </p>
            <p className="text-sm text-gray-600">
              {pointsEarned} / {question.points} points
            </p>
          </div>
        </div>

        {/* Expand Icon */}
        <div className="text-gray-400">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          {/* Question Text */}
          <div className="mt-4 mb-4">
            <p className="text-sm font-semibold text-gray-500 mb-2">Question:</p>
            <div className="text-base text-gray-900 leading-relaxed">
              <SafeMathText text={question.questionText} />
            </div>
          </div>

          {/* Question Image */}
          {question.imageUrl && (
            <div className="mb-6">
              <div className="relative w-full max-h-[400px] min-h-[200px] bg-slate-50 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                <img
                  src={question.imageUrl}
                  alt={question.imageCaption || "Question visual"}
                  className="w-full h-full object-contain max-h-[400px]"
                  loading="lazy"
                />
              </div>
              {question.imageCaption && (
                <p className="text-xs text-slate-600 mt-2 italic px-2">
                  {question.imageCaption}
                </p>
              )}
            </div>
          )}

          {/* Answer Details Based on Question Type */}
          {question.type === 'multiple-choice' && (
            <MultipleChoiceReview
              question={question}
              userAnswer={userAnswer}
            />
          )}

          {question.type === 'short-answer' && (
            <ShortAnswerReview
              question={question}
              userAnswer={userAnswer}
              isCorrect={isCorrect}
            />
          )}

          {question.type === 'true-false' && (
            <TrueFalseReview
              question={question}
              userAnswer={userAnswer}
              isCorrect={isCorrect}
            />
          )}

          {/* Explanation */}
          {question.explanation && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-1">Explanation:</p>
              <div className="text-sm text-blue-800">
                <SafeMathText text={question.explanation} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Multiple Choice Review
const MultipleChoiceReview = ({ question, userAnswer }) => {
  const selectedIds = question.allowMultipleAnswers && Array.isArray(userAnswer)
    ? userAnswer
    : [userAnswer];

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-gray-700 mb-2">Options:</p>
      {question.options.map((option, index) => {
        const isSelected = selectedIds.includes(option.id);
        const isCorrect = option.isCorrect;
        const letter = String.fromCharCode(65 + index);

        let bgColor = 'bg-white';
        let borderColor = 'border-gray-300';
        let textColor = 'text-gray-900';

        if (isSelected && isCorrect) {
          bgColor = 'bg-green-50';
          borderColor = 'border-green-500';
          textColor = 'text-green-900';
        } else if (isSelected && !isCorrect) {
          bgColor = 'bg-red-50';
          borderColor = 'border-red-500';
          textColor = 'text-red-900';
        } else if (!isSelected && isCorrect) {
          bgColor = 'bg-green-50';
          borderColor = 'border-green-300';
          textColor = 'text-green-800';
        }

        return (
          <div
            key={option.id}
            className={`px-4 py-3 rounded-lg border-2 ${bgColor} ${borderColor}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {isSelected && (
                  isCorrect ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )
                )}
                {!isSelected && isCorrect && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </div>
              <div className="flex-1">
                <span className={`font-medium ${textColor}`}>
                  {letter}. <SafeMathText text={option.text} />
                </span>
                {isSelected && !isCorrect && (
                  <p className="text-xs text-red-600 mt-1">Your answer</p>
                )}
                {!isSelected && isCorrect && (
                  <p className="text-xs text-green-600 mt-1">Correct answer</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Short Answer Review
const ShortAnswerReview = ({ question, userAnswer, isCorrect }) => {
  return (
    <div>
      <div className={`p-4 rounded-lg border-2 mb-3 ${
        isCorrect
          ? 'bg-green-50 border-green-500'
          : 'bg-red-50 border-red-500'
      }`}>
        <p className="text-sm font-semibold text-gray-700 mb-2">Your Answer:</p>
        <p className={`${isCorrect ? 'text-green-900' : 'text-red-900'}`}>
          {userAnswer || '(No answer provided)'}
        </p>
      </div>

      <div className="p-4 bg-green-50 border-2 border-green-300 rounded-lg">
        <p className="text-sm font-semibold text-gray-700 mb-2">Correct Answer(s):</p>
        <ul className="space-y-1">
          {question.correctAnswers?.map((answer, index) => (
            <li key={index} className="text-green-900">
              • {answer.value}
              {answer.isPrimary && (
                <span className="text-xs text-green-600 ml-2">(Primary)</span>
              )}
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-600 mt-2">
          Matching mode: {question.matchingMode?.replace(/-/g, ' ') || 'exact (case-insensitive)'}
        </p>
      </div>
    </div>
  );
};

// True/False Review
const TrueFalseReview = ({ question, userAnswer, isCorrect }) => {
  return (
    <div className="space-y-3">
      <div className={`p-4 rounded-lg border-2 ${
        isCorrect
          ? 'bg-green-50 border-green-500'
          : 'bg-red-50 border-red-500'
      }`}>
        <p className="text-sm font-semibold text-gray-700 mb-1">Your Answer:</p>
        <p className={`text-lg font-bold ${
          isCorrect ? 'text-green-900' : 'text-red-900'
        }`}>
          {userAnswer === true ? 'TRUE' : userAnswer === false ? 'FALSE' : '(No answer)'}
        </p>
      </div>

      <div className="p-4 bg-green-50 border-2 border-green-300 rounded-lg">
        <p className="text-sm font-semibold text-gray-700 mb-1">Correct Answer:</p>
        <p className="text-lg font-bold text-green-900">
          {question.correctAnswer ? 'TRUE' : 'FALSE'}
        </p>
      </div>
    </div>
  );
};

export default QuizReview;
